import { useEffect, useState } from 'react'
import PropTypes from '~/lib/propTypes'
import styled from '@emotion/styled'
import { Alert, Button, space } from '@ticketswap/solar'
import { Apple } from '@ticketswap/solar/icons'
import { AUTHENTICATION_STARTED, AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { track, setTrackingIdentity } from '~/utils/analytics'
import { AUTHENTICATE_CLIENT_FOR_TOKEN } from '~/graphql/mutations/authenticateClientForToken'
import useScript from '~/hooks/useScript'
import { useMutation } from '@apollo/client'
import { AUTHENTICATE_USER_WITH_APPLE } from '~/graphql/mutations/authenticateUserWithApple'
import getConfig from 'next/config'
import { setCookie } from 'cookies-next'
import { MONTH_IN_SECONDS } from '~/lib/timeInSeconds'

const { publicRuntimeConfig } = getConfig()

const StyledAlert = styled(Alert)`
  margin-block-end: ${space[8]};
`

const AppleAuthButton = ({ t, onSignup, onLogin, children }) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [loadingApple, setLoadingApple] = useState(false)
  const [appleAuth, setAppleAuth] = useState(null)
  const [loaded] = useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  )
  const [generate, { client }] = useMutation(AUTHENTICATE_CLIENT_FOR_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_APPLE)

  useEffect(() => {
    if (loaded && window.AppleID) {
      window.AppleID.auth.init({
        clientId: 'com.ticketswap.signinwithapplewebservice',
        scope: 'name email',
        redirectURI: `${window.location.protocol}//${window.location.host}`,
        usePopup: true,
      })

      setAppleAuth(window.AppleID.auth)
    }
  }, [loaded, appleAuth])

  const handleCompleted = async appleData => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: publicRuntimeConfig.NEXT_PUBLIC_CLIENT_ID,
        },
      },
    })

    const input = {
      token: authenticateClientForToken.token,
      appleAccessToken: appleData.authorization.id_token,
    }

    if (appleData.user) {
      input.firstName = appleData.user.name.firstName
      input.lastName = appleData.user.name.lastName
    }

    let data

    try {
      const {
        data: { appleAuth: appleAuthData },
      } = await authenticate({
        variables: { input },
      })
      data = appleAuthData
    } catch (error) {
      return handleError({ error: error.message })
    }
    const { tokenString } = data.apiAccessToken

    setCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME, tokenString, {
      maxAge: MONTH_IN_SECONDS,
      secure: true,
      sameSite: 'lax',
    })

    await client.resetStore()

    await setTrackingIdentity()

    if (data.isNew) {
      return onSignup(appleData.user.name)
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'apple',
      isNew: data.isNew,
    })

    onLogin()
  }

  const handleError = ({ error }) => {
    setLoadingApple(false)

    if (error === 'popup_closed_by_user') return

    if (error === 'popup_blocked_by_browser') {
      setErrorMessage(t('common:generic.popupBlocked'))
    } else {
      setErrorMessage(t('common:generic.somethingWentWrong'))
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'apple',
      error,
    })
  }

  return (
    <>
      {errorMessage && <StyledAlert variant="error">{errorMessage}</StyledAlert>}
      <Button
        loading={loadingApple}
        disabled={!loaded}
        fullWidth
        variant="apple"
        leftAdornment={<Apple size={24} />}
        onClick={async () => {
          setErrorMessage(null)
          setLoadingApple(true)
          track(AUTHENTICATION_STARTED, {
            method: 'apple',
          })

          try {
            const data = await appleAuth.signIn()
            handleCompleted(data)
          } catch (error) {
            handleError(error)
          }
        }}
      >
        {children}
      </Button>
    </>
  )
}

AppleAuthButton.propTypes = {
  onSignup: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
}

export default AppleAuthButton
